// src/features/operacion/components/OperacionPage.tsx
//
// «Operación» (w7.u2, CF S6 "GM weekly page") — route /operacion,
// Dirección. Six director cards with delta and trend marker
// (`GET /api/v2/ui/operacion`, w7.u1), then the supporting panels (step
// funnel, times, errors/abandon reasons, pre-check-in, identification),
// grouped from the read API's flat `panels` list. Scoped to exactly one
// property per request (`PropertySelector`) — unlike the rest of Dirección,
// which aggregates a multi-hotel scope.
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  TodayRounded as ArrivalsIcon,
  HowToRegRounded as SelfServiceIcon,
  ShieldRounded as ContainmentIcon,
  VpnKeyRounded as TimeToKeyIcon,
  ReportProblemRounded as IncidentsIcon,
  FactCheckRounded as PreCheckinIcon,
} from '@mui/icons-material';
import { useDataProvider, useLocaleState, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { WidgetCard } from '@/components/common/WidgetCard';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { metricExportText, metricLabel } from '@/lib/v2MetricView';
import { buildThreeSentencesView } from '@/lib/v2ThreeSentences';
import type { GoodDirection } from '@/components/common/DeltaChip';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { OperacionCard, type OperacionCardKind } from './OperacionCard';
import { MetricPanelSection } from '@/features/insight-v2-shared/components/MetricPanelSection';
import type { MetricResultDto, OperacionCardDto, OperacionResponse } from '@/types/analyticsV2';

// The six cards the read API states (V2UiOperacionController.CardIds), each
// with its value kind and the direction that counts as an improvement:
// arrivals have none (more or fewer guests is neither good nor bad), a
// shorter time to key and fewer incidents are better.
const CARDS: { id: string; kind: OperacionCardKind; good: GoodDirection; icon: React.ReactNode }[] = [
  { id: 'ops.arrivals', kind: 'count', good: 'none', icon: <ArrivalsIcon /> },
  { id: 'ops.self_service_completed', kind: 'count', good: 'up', icon: <SelfServiceIcon /> },
  { id: 'ops.containment', kind: 'share', good: 'up', icon: <ContainmentIcon /> },
  { id: 'ops.time_to_key_median', kind: 'duration', good: 'down', icon: <TimeToKeyIcon /> },
  { id: 'ops.pre_checkin_share', kind: 'share', good: 'up', icon: <PreCheckinIcon /> },
  { id: 'ops.incidents', kind: 'count', good: 'down', icon: <IncidentsIcon /> },
];

const PANEL_GROUPS: { key: string; sectionKey: string; ids: string[] }[] = [
  {
    key: 'stepFunnel',
    sectionKey: 'resources.operacion.sections.stepFunnel',
    ids: ['ops.step_funnel', 'ops.stall_share', 'ops.journeys_started', 'ops.journeys_by_outcome', 'ops.completion_rate', 'ops.handed_to_desk'],
  },
  {
    key: 'times',
    sectionKey: 'resources.operacion.sections.times',
    ids: ['ops.time_to_key_p90', 'ops.journey_duration_median', 'ops.time_on_step_median'],
  },
  {
    key: 'errorsAbandon',
    sectionKey: 'resources.operacion.sections.errorsAbandon',
    ids: ['ops.errors_by_component', 'ops.error_rate', 'ops.abandon_reasons'],
  },
  {
    key: 'preCheckin',
    sectionKey: 'resources.operacion.sections.preCheckin',
    ids: ['ops.pre_checkin_funnel', 'ops.pre_checkin_resumed'],
  },
  {
    key: 'identification',
    sectionKey: 'resources.operacion.sections.identification',
    ids: ['ops.attempt_fallback'],
  },
];

export const OperacionPage: React.FC = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dataProvider = useDataProvider();
  const property = useV2PropertySelection('operacion');
  const exportCsv = useCsvExport({ page: 'operacion' });
  const canExport = hasResourcePermission('operacion', 'export');

  const { propertyId } = property;

  const [period, setPeriod] = useAnalyticsPeriod();

  const { data, loading, error, refresh } = useAnalyticsFetch<OperacionResponse>(
    () => (dataProvider as any).getAnalyticsOperacion({ property: propertyId, from: period.from, to: period.to }),
    [dataProvider, propertyId, period.from, period.to],
    Boolean(propertyId)
  );

  const cardsById = useMemo(() => {
    const map = new Map<string, OperacionCardDto>();
    (data?.cards ?? []).forEach((c) => map.set(c.current.id, c));
    return map;
  }, [data]);

  const panelsById = useMemo(() => {
    const map = new Map<string, MetricResultDto>();
    (data?.panels ?? []).forEach((p) => map.set(p.id, p));
    return map;
  }, [data]);

  const threeSentences = panelsById.get('ops.three_sentences');
  const changes = buildThreeSentencesView(threeSentences, translate);
  const groupedIds = new Set(PANEL_GROUPS.flatMap((g) => g.ids).concat(['ops.three_sentences']));
  const otherPanels = (data?.panels ?? []).filter((p) => !groupedIds.has(p.id));

  const handleExport = async () => {
    if (!data) return;
    const rows = [...data.cards.map((c) => c.current), ...data.panels.filter((p) => p.id !== 'ops.three_sentences' || changes.kind !== 'hidden')];
    await exportCsv(
      rows,
      [
        { labelKey: 'resources.operacion.export.metric', value: (r: MetricResultDto) => metricLabel(r, translate) },
        {
          labelKey: 'resources.operacion.export.value',
          value: (r: MetricResultDto) =>
            r.id === 'ops.three_sentences'
              ? changes.kind === 'changes'
                ? changes.sentences.join(' ')
                : translate('resources.operacion.noChanges')
              : metricExportText(r, translate, locale),
        },
      ],
      { from: period.from, to: period.to }
    );
  };

  return (
    <Box>
      <SectionHeader manualScreen="operacion"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
          </Box>
        }
        subtitle={translate('resources.operacion.subtitle')}
        helpConfig={{
          titleKey: 'resources.operacion.help.title',
          descriptionKey: 'resources.operacion.help.description',
          featureKeys: ['resources.operacion.help.features.cards', 'resources.operacion.help.features.panels'],
          tipKeys: ['resources.operacion.help.tips.property'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <>
          {error && <FetchErrorBanner onRetry={refresh} />}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 2.5 }}>
            {CARDS.map((c) => (
              <OperacionCard key={c.id} card={cardsById.get(c.id)} kind={c.kind} icon={c.icon} goodDirection={c.good} loading={loading} />
            ))}
          </Box>

          {/* Absent (the periods cannot be compared): nothing is said. */}
          {!loading && threeSentences && changes.kind !== 'hidden' && (
            <WidgetCard title={metricLabel(threeSentences, translate)} sx={{ mb: 2.5 }}>
              {changes.kind === 'none' ? (
                <Typography variant="body2" color="text.secondary">
                  {translate('resources.operacion.noChanges')}
                </Typography>
              ) : (
                changes.sentences.map((sentence) => (
                  <Typography key={sentence} variant="body2" sx={{ mb: 0.5 }}>
                    {sentence}
                  </Typography>
                ))
              )}
            </WidgetCard>
          )}

          {PANEL_GROUPS.map((group) => (
            <MetricPanelSection
              key={group.key}
              title={translate(group.sectionKey)}
              metrics={group.ids.map((id) => panelsById.get(id)).filter((m): m is MetricResultDto => Boolean(m))}
              loading={loading}
            />
          ))}

          <MetricPanelSection title={translate('resources.operacion.sections.other')} metrics={otherPanels} loading={loading} />
        </>
      )}
    </Box>
  );
};

export default OperacionPage;
